import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { AuthService } from "../services/auth.service";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.scss",
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  mfaForm: FormGroup;
  forgotPasswordForm: FormGroup;
  confirmForgotPasswordForm: FormGroup; 
  resetPasswordForm: FormGroup;
  verifyEmailForm: FormGroup;
  verifyEmailCodeForm: FormGroup;
  resetPasswordFlow = false;
  errorMessage: string | null = null;
  forgotPasswordFlow = false;
  isLoading = false;
  showPassword = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  mfaChallengeName = "";
  mfaAccessToken = "";
  mfaIdToken = "";
  mfaRequired = false;
  mfaSessionKey = "";
  mfaEmail = "";
  mfaSecretCode = "";
  forceMfa = true;
  mfaSetup = false;
  mfaVerify = false;
  mfaVerificationSuccessful = false;
  countdown = 5;
  countdownInterval: any;
  unconfirmedUserEmail = "";
  showEmailConfirmationPrompt = false;
  showConfirmCodeInput = false;
  emailVerificationSuccessful = false;
  
  confirmForgotPasswordFlow = false;
  showConfirmForgotPassword = false;
  confirmPasswordSuccessful = false;

  @ViewChild("mfaVerifyInput") mfaVerifyInput!: ElementRef;
  @ViewChild("mfaRequiredField") mfaRequiredField!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required]],
    });

    this.forgotPasswordForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
    });

    this.confirmForgotPasswordForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      code: ["", [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      newPassword: [
        "",
        [
          Validators.required,
          Validators.minLength(8),
          this.containsSymbolValidator(),
          this.containsUppercaseValidator(),
        ]
      ]
    })

    this.resetPasswordForm = this.fb.group(
      {
        previousPassword: ["", Validators.required],
        newPassword: [
          "",
          [
            Validators.required,
            Validators.minLength(8),
            this.containsSymbolValidator(),
            this.containsUppercaseValidator(),
          ],
        ],
        confirmPassword: ["", [Validators.required]],
      },
      {
        validators: this.passwordsMatchValidator,
      }
    );

    this.mfaForm = this.fb.group({
      mfaCode: [
        "",
        [
          Validators.required,
          Validators.pattern(/^\d{6}$/),
          Validators.minLength(6), // Ensures minimum length of 6
          Validators.maxLength(6),
        ],
      ],
    });

    this.verifyEmailForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
    });

    this.verifyEmailCodeForm = this.fb.group({
      code: [
        "",
        [
          Validators.required,
          Validators.pattern(/^\d{6}$/),
          Validators.minLength(6),
          Validators.maxLength(6),
        ],
      ],
    });
  }

  ngOnInit(): void { 
    const navState = history.state;
    const prefilledEmail = navState?.email;
    const prefilledPassword = navState?.password;
  
    if(prefilledEmail && prefilledPassword){
      this.errorMessage = null;
      this.loginForm.patchValue({
        email: prefilledEmail,
        password: prefilledPassword
      });
      this.isLoading = true;
      
      this.login(prefilledEmail, prefilledPassword);
    }
  }

  ngAfterViewInit(): void {
    this.checkRouteParamsForForgotPassword();
    this.focusMfaRequiredInput();
    this.focusMfaVerifyInput();
    this.errorMessage = null;
  }

  private checkRouteParamsForForgotPassword(): void {
    const mode = this.route.snapshot.queryParamMap.get("mode");
    if (mode === "forgot-password") {
      this.forgotPasswordFlow = true;
      this.cdr.detectChanges();
    }
  }

  toggleForgotPassword() {
    this.forgotPasswordFlow = !this.forgotPasswordFlow;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(){
    this.showConfirmPassword = !this.showConfirmPassword
  }

  toggleConfirmForgotPasswordVisibility(){
    this.showConfirmForgotPassword = !this.showConfirmForgotPassword;
  }

  focusMfaVerifyInput(): void {
    if (this.mfaVerifyInput) {
      this.mfaVerifyInput.nativeElement.focus();
    }
  }

  focusMfaRequiredInput(): void {
    if (this.mfaRequiredField) {
      this.mfaRequiredField.nativeElement.focus();
    }
  }

  private initiateMfaSetup(email: string): void {
    this.isLoading = true;

    this.authService.initiateTotpSetup(this.mfaAccessToken).subscribe({
      next: (response) => {
        this.mfaEmail = email;
        this.mfaSecretCode = response.secretCode;
        this.mfaSetup = true;
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Failed to initiate MFA setup:", err);
        this.errorMessage =
          "An error occurred while setting up MFA. Please try again.";
        this.isLoading = false;
      },
    });
  }

  onMfaSetupComplete() {
    this.mfaSetup = false; // Hide the QR code setup screen
    this.mfaVerify = true;
    this.cdr.detectChanges();
    this.focusMfaVerifyInput();
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.errorMessage = null;
      this.isLoading = true;
      const { email, password } = this.loginForm.value;
      
      this.login(email, password);
      
    } else {
      this.errorMessage = "Please fill out the form correctly.";
    }
  }

  login(email: string, password: string){
    this.authService.login(email, password).subscribe({
      next: (response) => {
        if (response.challengeName == "SOFTWARE_TOKEN_MFA") {
          this.mfaChallengeName = "SOFTWARE_TOKEN_MFA";
          this.mfaRequired = true;
          this.mfaSessionKey = response.session; // Save the session for MFA verification
          this.mfaEmail = email;
          this.isLoading = false;
          this.cdr.detectChanges();

          this.focusMfaRequiredInput();
        } else {
          // this.authService.handleAuthTokens(response);
          
          if (this.authService.isAdmin(response.idToken) || this.authService.isStaff(response.idToken)) {
            this.mfaAccessToken = response.accessToken;
            this.mfaIdToken = response.idToken;
            this.initiateMfaSetup(email);
          } else {
            console.log("MFA is disabled for users");
            this.authService.handleAuthTokens(response);
            
            // Redirect to dashboard or desired page
            const returnUrl = this.route.snapshot.queryParams["returnUrl"];
            if (returnUrl) {
              this.router.navigate([returnUrl]);
              // this.isLoading = false;
            } else {
              // TODO: Navigate to appropriate screen
              this.router.navigate(['']);                
            }
          }
        }
      },
      error: (err) => {
        console.error("Login failed:", err);
        this.isLoading = false;

        if (
          err.error.error === "New password is required to complete login." ||  err.error.error === "Password reset is required for this account."
        ) {
          this.errorMessage = null; // Clear the error message
          this.resetPasswordFlow = true; // Toggle to reset password UI
        } else if (
          err.error.error === "User account is not confirmed. Please confirm your email."
        ) {
          this.unconfirmedUserEmail = this.loginForm.get("email")?.value;
          this.showEmailConfirmationPrompt = true;
          this.verifyEmailForm.controls['email'].setValue(this.unconfirmedUserEmail);
          this.verifyEmailForm.controls['email'].disable();
        } else if (err.status === 401) {
          this.errorMessage = err.error.error;
        } else {
          this.errorMessage = "Unknown error occurred. Please try again.";
        }
      },
    });
  }

  onSubmitMfa(): void {
    this.isLoading = true;
    const { mfaCode } = this.mfaForm.value;

    this.authService
      .respondMfa(
        this.mfaSessionKey,
        this.mfaEmail,
        mfaCode,
        this.mfaChallengeName
      )
      .subscribe({
        next: (response) => {
          this.authService.handleAuthTokens(response);
          const returnUrl = this.route.snapshot.queryParams["returnUrl"];
          if (returnUrl) {
            this.router.navigate([returnUrl]);
          } else {
            this.router.navigate(['']);
          }
        },
        error: (err) => {
          console.error("MFA failed:", err.error.error);
          this.errorMessage = err.error.error;
          this.isLoading = false;

          this.cdr.detectChanges();
          setTimeout(() => {
            this.errorMessage = null;
          }, 10000);
        },
      });
  }

  onSubmitMfaVerification(): void {
    if (this.mfaForm.valid) {
      this.isLoading = true;
      const mfaCode = this.mfaForm.get("mfaCode")?.value;

      this.authService.verifyTotpSetup(this.mfaAccessToken, mfaCode).subscribe({
        next: (response) => {
          // Redirect to login
          this.authService.handleAuthTokens({accessToken: this.mfaAccessToken, idToken: this.mfaIdToken})
          this.authService.enableMfa().subscribe({
            next: (response) => {
              this.isLoading = false;
              this.mfaForm.get("mfaCode")?.reset();
              this.isLoading = true;
              this.router.navigate(['']);
            },
            error: (err) => {
              this.isLoading = false;
              // this.authService.logout();
              this.errorMessage = "Failed to enable MFA. Please try again.";
            },
          });
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = "Failed to verify code. Please try again.";

          setTimeout(() => {
            this.errorMessage = null;
          }, 10000);
        },
      });
    }
  }

  resendConfirmationEmail(): void {
    this.isLoading = true;
    this.authService.resendSignupCode(this.unconfirmedUserEmail).subscribe({
      next: () => {
        this.isLoading = false;
        // this.errorMessage = "Verification email has been sent. Please check your inbox.";
        this.showEmailConfirmationPrompt = false;
        this.showConfirmCodeInput = true; // if you're showing a code input afterward
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error("Resend failed:", err);
        this.errorMessage =
          "Failed to resend confirmation email. Please try again.";
      },
    });
  }

  verifyConfirmationCode(): void {
    this.isLoading = true;
    const code = this.verifyEmailCodeForm.controls['code'].value
    this.authService.verifySignupCode(this.unconfirmedUserEmail, code).subscribe({
      next: () => {
        this.isLoading = false;
        this.emailVerificationSuccessful = true;
        this.countdownInterval = setInterval(() => {
          this.countdown--;
          if (this.countdown === 0) {
            clearInterval(this.countdownInterval);
            this.emailVerificationSuccessful = false;
            this.showConfirmCodeInput = false; // Redirect to login
          }
        }, 1000);
        
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error("Resend failed:", err);
        this.errorMessage =
          "Failed to Verify signup code";
      },
    });
  }

  onResetPassword(): void {
    if (this.resetPasswordForm.valid) {
      this.isLoading = true;
      const { email } = this.loginForm.value; // Use the email from the login form
      const { previousPassword, newPassword } = this.resetPasswordForm.value;

      this.authService
        .resetPassword(email, previousPassword, newPassword)
        .subscribe({
          next: (response) => {
            this.authService.handleAuthTokens(response);
            this.isLoading = false;
            this.resetPasswordFlow = false; // Toggle back to login UI
            // this.errorMessage = "Password reset successfully. Please log in.";

            // Redirect to dashboard or desired page
            const returnUrl = this.route.snapshot.queryParams["returnUrl"];
            if (returnUrl) {
              this.router.navigate([returnUrl]);
            } else {
              // TODO: Navigate to appropriate screen
              this.router.navigate(["questionnaire"]);
            }
          },
          error: (err: any) => {
            console.error("Password reset failed:", err);
            this.isLoading = false;
            this.errorMessage = "Failed to reset password. Please try again.";
          },
        });
    }
  }

  onForgotPassword(): void { 
    console.log("forgot password clicked");
    if (this.forgotPasswordForm.invalid) return;
    this.isLoading = true;
   
    this.errorMessage = '';

    const { email } = this.forgotPasswordForm.value;

    this.authService.forgotPassword(email).subscribe({
      next: () => {
        this.forgotPasswordForm.reset();
        this.forgotPasswordFlow = false;
        this.confirmForgotPasswordFlow = true;
        this.confirmForgotPasswordForm.controls['email'].setValue(email);
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Something went wrong.';
        this.isLoading = false;
      }
    });
  }

  onConfirmForgotPassword(): void {
    if (this.confirmForgotPasswordForm.invalid) {
      this.confirmForgotPasswordForm.markAllAsTouched();
      return;
    }
  
    this.isLoading = true;
    this.errorMessage = '';
  
    const { code, newPassword } = this.confirmForgotPasswordForm.value;
    const email = this.confirmForgotPasswordForm.getRawValue().email; // get value from disabled input
  
    this.authService.confirmForgotPassword(email, code, newPassword).subscribe({
      next: () => {
        this.isLoading = false;
        this.countdown = 5;
        this.confirmPasswordSuccessful = true;
        
        this.countdownInterval = setInterval(() => {
          this.countdown--;
          if (this.countdown === 0) {
            clearInterval(this.countdownInterval);
            this.confirmPasswordSuccessful = false;
            this.forgotPasswordFlow = false;
            this.confirmForgotPasswordFlow = false;
          }
        }, 1000);
        
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err?.error?.message || 'Failed to reset password. Please try again.';
      }
    });
  }

  toggleConfirmBackToLogin(): void { 
    this.confirmForgotPasswordFlow = false;
    this.forgotPasswordFlow = false;
    this.errorMessage = '';
    this.confirmForgotPasswordForm.reset();
  }

  passwordsMatchValidator(form: FormGroup) {
    const password = form.get("newPassword")?.value;
    const confirmPassword = form.get("confirmPassword")?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  containsSymbolValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Return null if the control value is empty or doesn't exist
      }

      const hasSymbol = /[!@#$%^&*(),.?":{}|<>_]/.test(control.value);
      return hasSymbol ? null : { missingSymbol: true }; // Return error if no symbol
    };
  }

  containsUppercaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Return null if the control value is empty or doesn't exist
      }

      const hasUppercase = /[A-Z]/.test(control.value);
      return hasUppercase ? null : { missingUppercase: true }; // Return error if no uppercase letter
    };
  }
}
